import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cacheAdhanSound,
  currentNotificationPermission,
  isNativeShell,
  isNotificationSupported,
  openNotificationSettings,
  requestNotificationPermission,
  schedulePrayerNotifications,
  stopAdhan,
} from '../utils/prayerNotifications';
import { DEFAULT_ATHKAR_DELAY_MIN } from '../utils/notificationPlan';

// المتصفح يجدول يومين، فمراجعتها كل ستّ ساعات تكفي لتعبر منتصف الليل
// حتى لو بقي التطبيق مفتوحاً بلا تفاعل. (أندرويد يعيد التسليح بنفسه.)
const REFRESH_MS = 6 * 60 * 60 * 1000;

// العودة إلى التطبيق تطلق focus وvisibilitychange معاً؛ ما جاء خلال ثانية عودةٌ واحدة
const RESUME_DEBOUNCE_MS = 1000;

/**
 * تنبيهات الأذان — دائمة بلا مفتاح.
 *
 * كانت الجدولة داخل قسم المواقيت، وكانت تُلغى في تنظيف الأثر؛ فإذا غادر
 * المستخدم التبويب ضاعت تنبيهاته. فصارت هنا: يُركَّب هذا الخطّاف مرة
 * واحدة على مستوى التطبيق فتبقى الجدولة حيّة أياً كان المعروض.
 *
 * ما لا يملكه التطبيق: إذنُ النظام. أندرويد ١٣ فأحدث يسأل المستخدم،
 * وله أن يرفض وأن يسحب الإذن لاحقاً. فتُراجَع الحالة عند كل عودة إلى
 * التطبيق — لأن المستخدم قد يكون غيّرها في الإعدادات للتوّ.
 *
 * @returns {{permission: string, supported: boolean, openSettings: () => Promise<boolean>}}
 */
export function useAdhanNotifications({
  lat,
  lng,
  methodId,
  madhab,
  timeZone,
  cityName,
  adhanUrl = '',
  athkar = true,
  athkarDelayMin = DEFAULT_ATHKAR_DELAY_MIN,
}) {
  const [permission, setPermission] = useState('prompt');
  const [tick, setTick] = useState(0);
  const askedRef = useRef(false);

  // مسار الأذان على الجهاز. فارغ يعني: استعمل المضمَّن في التطبيق.
  const [adhanPath, setAdhanPath] = useState('');

  useEffect(() => {
    if (!isNativeShell() || !adhanUrl) {
      setAdhanPath('');
      return;
    }
    let cancelled = false;
    cacheAdhanSound(adhanUrl).then((path) => {
      if (!cancelled) setAdhanPath(path);
    });
    return () => {
      cancelled = true;
    };
  }, [adhanUrl]);

  const supported = isNotificationSupported();

  // مزامنة حالة الإذن، وطلبه مرة واحدة إن لم يُسأل عنه بعد
  const syncPermission = useCallback(async () => {
    if (!isNotificationSupported()) {
      setPermission('unsupported');
      return;
    }

    let state = await currentNotificationPermission();

    if (state === 'prompt' && !askedRef.current) {
      askedRef.current = true;
      await requestNotificationPermission();
      // لا نثق بما يُرجعه الطلب: تسجيلُ الإشعارات المرسلة يطلب الإذن
      // نفسه عند الإقلاع، وأندرويد يعرض نافذة واحدة لطلبين متزامنين —
      // فيأخذ أحدُهما جواب المستخدم ويأخذ الآخر رفضاً ليس منه.
      // الحالة بعد الطلب وحدها هي الصادقة.
      state = await currentNotificationPermission();
    }

    setPermission(state);
  }, []);

  useEffect(() => {
    syncPermission();
  }, [syncPermission]);

  // العودة إلى التطبيق: الإذن قد يكون تغيّر في إعدادات النظام،
  // واليوم قد يكون انقلب فتلزم إعادة جدولة
  useEffect(() => {
    let lastResume = 0;
    const onResume = () => {
      if (document.visibilityState !== 'visible') return;
      // وإلا أُعيدت الجدولة مرتين لكل عودة، وعلى أندرويد خطةُ سنةٍ كاملة مرتين
      const t = Date.now();
      if (t - lastResume < RESUME_DEBOUNCE_MS) return;
      lastResume = t;
      syncPermission();
      setTick((t) => t + 1);
    };

    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    const timer = setInterval(() => setTick((t) => t + 1), REFRESH_MS);

    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      clearInterval(timer);
    };
  }, [syncPermission]);

  // إيقاف الأذان بأي زر (الرجوع، لوحة المفاتيح، أو أزرار الجوال)
  useEffect(() => {
    const handleKey = () => {
      stopAdhan();
    };
    window.addEventListener('keydown', handleKey);

    let backSub = null;
    if (window.Capacitor?.Plugins?.App) {
      window.Capacitor.Plugins.App.addListener('backButton', () => {
        stopAdhan();
      }).then((sub) => {
        backSub = sub;
      }).catch(() => {});
    }

    return () => {
      window.removeEventListener('keydown', handleKey);
      if (backSub?.remove) backSub.remove();
    };
  }, []);

  // الجدولة نفسها. لا تنظيف يُلغيها: schedulePrayerNotifications تمسح
  // القديم قبل أن تكتب الجديد، فالإلغاء هنا كان يفرّغها عند كل تنقّل.
  useEffect(() => {
    if (permission !== 'granted') return;
    if (typeof lat !== 'number' || typeof lng !== 'number') return;

    schedulePrayerNotifications({
      lat,
      lng,
      methodId,
      madhab,
      timeZone,
      cityName,
      adhanSound: adhanPath,
      athkar,
      athkarDelayMin,
    });
  }, [permission, lat, lng, methodId, madhab, timeZone, cityName, adhanPath, athkar, athkarDelayMin, tick]);

  return { permission, supported, openSettings: openNotificationSettings, stopAdhan };
}
